import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight, 
  CheckCircle2, 
  Package, 
  Search, 
  BarChart3, 
  Shield, 
  ClipboardCheck, 
  RefreshCw,
  FileText,
  Upload,
  Database,
  FolderCheck,
  Settings
} from 'lucide-react';

const AjioServices = () => {
  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Ajio Account Management Services India | Obiquiste Solutions</title>
        <meta name="description" content="Professional Ajio seller account management: onboarding, product listing, catalog optimization & growth strategies. Expand your brand on Ajio marketplace." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/ajio-account-management-services" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">AJIO Account Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-indigo-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                AJIO Account Management <span className="bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">Services</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                Complete AJIO Seller Account Management for Fashion & Lifestyle Brands
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8">
                Amazing Sales provides complete AJIO account management services to help brands improve visibility, manage catalog operations, and grow sales on AJIO marketplace. Our expert team handles everything from product listings to performance optimization, ensuring your brand achieves maximum potential on one of India's leading fashion marketplaces.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?service=ajio">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free AJIO Consultation
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=ajio">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to AJIO Expert
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-indigo-400/20 to-purple-400/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white rounded-2xl shadow-2xl p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-indigo-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-indigo-600">500+</div>
                      <div className="text-sm text-slate-600">Products Listed</div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-purple-600">98%</div>
                      <div className="text-sm text-slate-600">Catalog Accuracy</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">3x</div>
                      <div className="text-sm text-slate-600">Visibility Boost</div>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-orange-600">24/7</div>
                      <div className="text-sm text-slate-600">Support</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Why Choose Amazing Sales for AJIO Account Management?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              AJIO has emerged as one of India's premier fashion and lifestyle marketplaces, offering brands access to millions of fashion-conscious consumers. At Amazing Sales, we understand the unique dynamics of the AJIO marketplace and provide comprehensive account management services tailored to help your brand stand out, attract customers, and drive sustainable growth.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our dedicated team of AJIO specialists handles every aspect of your seller account, from initial setup and catalog optimization to ongoing performance monitoring and strategic growth initiatives. We ensure your brand maintains a strong presence on AJIO while you focus on what you do best – creating great products.
            </p>
          </motion.div>
        </div>
      </section>

      {/* AJIO Account Management Services Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              AJIO Account Management Services
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Complete end-to-end account management to help your brand thrive on AJIO marketplace
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Product Listing & Catalog Management */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6">
                <Package className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Product Listing & Catalog Management
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We create and manage optimized product listings with proper titles, descriptions, attributes, and category placement. Our team ensures every product is accurately represented with compelling content that attracts buyers and improves conversion rates.
              </p>
              <ul className="space-y-2">
                {['Professional listing creation', 'Accurate product attributes', 'Proper category placement', 'Quality image guidelines'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* AJIO SEO & Search Optimization */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Search className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                AJIO SEO & Search Optimization
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We optimize product titles, descriptions, keywords, and attributes to improve product ranking and visibility on AJIO search. Our SEO strategies are specifically tailored for AJIO's search algorithm to ensure your products appear in relevant searches and reach more potential customers.
              </p>
              <ul className="space-y-2">
                {['Keyword-rich titles', 'Optimized descriptions', 'Strategic attribute selection', 'Search ranking improvement'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Price & Inventory Management */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Price & Inventory Management
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We regularly monitor pricing and inventory levels to maintain competitive listings and avoid stock issues. Our proactive approach ensures your products remain competitively priced while maintaining healthy profit margins and preventing stockouts that could hurt your seller score.
              </p>
              <ul className="space-y-2">
                {['Competitive price monitoring', 'Real-time inventory tracking', 'Stock level alerts', 'Margin optimization'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Category Approval Support */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Category Approval Support
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                If a category approval is required, our team helps obtain the approval through AJIO support. We assist brands in submitting the required information and coordinate with AJIO support to get the category approved. Our experience with AJIO's approval process ensures faster approvals and fewer rejections.
              </p>
              <ul className="space-y-2">
                {['Documentation preparation', 'Application submission', 'AJIO support coordination', 'Approval follow-up'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Order & Performance Monitoring */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <ClipboardCheck className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Order & Performance Monitoring
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We monitor orders, cancellations, returns, and account health to maintain smooth operations and a strong seller score. Our team proactively identifies and addresses any performance issues before they impact your account standing or customer satisfaction.
              </p>
              <ul className="space-y-2">
                {['Order tracking & management', 'Return handling', 'Cancellation monitoring', 'Seller score optimization'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Account Health Management */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <RefreshCw className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4 text-slate-900">
                Account Health Management
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We continuously monitor your AJIO account health metrics and take proactive measures to maintain excellent standing. Our team ensures compliance with AJIO policies and addresses any issues promptly to protect your seller account.
              </p>
              <ul className="space-y-2">
                {['Health metrics monitoring', 'Policy compliance', 'Issue resolution', 'Performance reporting'].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AJIO Product Listing & Catalog Management Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              AJIO Product Listing & Catalog Management
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Professional product listing and catalog services to maximize your brand's visibility on AJIO
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Product Listing Creation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-slate-50 to-indigo-50 rounded-2xl p-8 border border-slate-200"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    Product Listing Creation
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We create professional product listings with optimized titles, detailed descriptions, product attributes, and keywords to improve visibility on AJIO marketplace. Each listing is crafted to highlight your product's unique features and appeal to your target audience.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {['SEO-optimized titles', 'Detailed descriptions', 'Accurate attributes', 'Strategic keywords'].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-indigo-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Catalog Setup & Bulk Upload */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl p-8 border border-slate-200"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    Catalog Setup & Bulk Upload
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We manage catalog creation, bulk product uploads, and proper product categorization to ensure accurate listings. Our streamlined process allows for efficient onboarding of large product catalogs while maintaining quality and accuracy.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {['Bulk upload management', 'Category mapping', 'Data validation', 'Error-free listings'].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Product Data Optimization */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-slate-50 to-green-50 rounded-2xl p-8 border border-slate-200"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    Product Data Optimization
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We optimize product attributes, backend details, and keywords to improve discoverability on AJIO search. Our data optimization strategies ensure your products are easily found by customers searching for relevant items.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {['Attribute optimization', 'Backend SEO', 'Keyword research', 'Search visibility'].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Category Approval Assistance */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-slate-50 to-orange-50 rounded-2xl p-8 border border-slate-200"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FolderCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    Category Approval Assistance
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    If category approval is required, our team helps obtain approval through AJIO support and ensures products are listed under the correct categories. We handle all documentation and coordination to expedite the approval process.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {['Application support', 'Document preparation', 'Category guidance', 'Fast-track approvals'].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-orange-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Catalog Updates & Maintenance - Full Width */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 border border-slate-200"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    Catalog Updates & Maintenance
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We update product information, attributes, and catalog details regularly to maintain accurate listings. Our ongoing maintenance ensures your catalog stays current, competitive, and compliant with AJIO's latest requirements and best practices.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {['Regular updates', 'Price adjustments', 'Stock synchronization', 'Content refresh', 'Image updates', 'Attribute corrections', 'Policy compliance', 'Quality assurance'].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Amazing Sales Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Choose Amazing Sales for AJIO Management?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'AJIO Expertise', desc: 'Deep understanding of AJIO marketplace dynamics and requirements' },
              { title: 'Dedicated Team', desc: 'Expert account managers focused on your brand success' },
              { title: 'Proven Results', desc: 'Track record of improving visibility and sales for fashion brands' },
              { title: 'Complete Support', desc: 'End-to-end management from setup to ongoing optimization' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-md border border-slate-200 text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Ready to Grow Your Brand on AJIO?
            </h2>
            <p className="text-xl mb-8 text-indigo-100">
              Let Amazing Sales handle your AJIO account management while you focus on building your brand. Get started with a free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?service=ajio">
                <Button className="bg-white text-indigo-600 hover:bg-indigo-50 px-8 py-6 rounded-full text-base font-semibold shadow-lg">
                  Get Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 rounded-full text-base font-semibold">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AjioServices;
